export interface ExperiencePageRequest {
  flowId: string;
  answers: Record<string, string>;
}

export function buildExperiencePageUrl(path: string, request: ExperiencePageRequest): string {
  const params = new URLSearchParams();
  params.set("flow", request.flowId);
  if (Object.keys(request.answers).length > 0) {
    params.set("answers", encodeAnswers(request.answers));
  }

  return `${path}?${params.toString()}`;
}

export function parseExperiencePageRequest(url: string | URL): ExperiencePageRequest | null {
  const parsedUrl = typeof url === "string" ? new URL(url, "https://gemsales.local") : url;
  const flowId = normalizeIdentifier(parsedUrl.searchParams.get("flow") ?? parsedUrl.searchParams.get("flow_id") ?? "");
  if (!flowId) return null;

  return {
    flowId,
    answers: decodeAnswers(parsedUrl.searchParams.get("answers")),
  };
}

export function encodeAnswers(answers: Record<string, string>): string {
  const filteredEntries = Object.entries(answers)
    .map<[string, string] | null>(([key, value]) => {
      const safeKey = normalizeIdentifier(key);
      const safeValue = normalizeAnswerValue(value);
      if (!safeKey || !safeValue) return null;

      return [safeKey, safeValue];
    })
    .filter((entry): entry is [string, string] => Boolean(entry))
    .sort(([a], [b]) => a.localeCompare(b));

  return new URLSearchParams(filteredEntries).toString();
}

export function decodeAnswers(encodedAnswers: string | null): Record<string, string> {
  if (!encodedAnswers) return {};

  const params = new URLSearchParams(encodedAnswers);
  const answers: Record<string, string> = {};
  params.forEach((value, key) => {
    const safeKey = normalizeIdentifier(key);
    const safeValue = normalizeAnswerValue(value);
    if (!safeKey || !safeValue) return;
    answers[safeKey] = safeValue;
  });

  return answers;
}

function normalizeIdentifier(value: string): string {
  const normalized = value.trim();
  if (!/^[a-zA-Z0-9_-]{1,80}$/.test(normalized)) return "";

  return normalized;
}

function normalizeAnswerValue(value: string): string {
  const normalized = value.trim();
  if (!/^[a-zA-Z0-9_:/.-]{1,120}$/.test(normalized)) return "";

  return normalized;
}
